import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  IconButton,
  Autocomplete,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import NotificationSnackbar from "./NotificationBar";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import GroupIcon from "@mui/icons-material/Group";

const AddNewPayrollGroup = ({ open, onClose, onSuccess }) => {
  const theme = useTheme();

  const [formData, setFormData] = useState({
    name: "",
    employeeGroupId: null,
  });

  const [employeeGroups, setEmployeeGroups] = useState([]);
  const [loadingEmployeeGroups, setLoadingEmployeeGroups] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };
  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Fetch Employee Groups
  const fetchEmployeeGroups = async () => {
    try {
      setLoadingEmployeeGroups(true);

      const response = await fetch("/api/support/employee-groups");

      if (!response.ok) {
        throw new Error(
          `Failed to fetch employee groups. Status: ${response.status}`,
        );
      }

      const data = await response.json();

      const list = Array.isArray(data)
        ? data
        : data.recordset || data.result || [];

      setEmployeeGroups(list);
    } catch (err) {
      console.error("Error fetching employee groups:", err);

      showSnackbar("Failed to load employee groups.", "error");
    } finally {
      setLoadingEmployeeGroups(false);
    }
  };

  // Reset and load data whenever modal opens
  useEffect(() => {
    if (open) {
      setFormData({
        name: "",
        employeeGroupId: null,
      });

      setError("");
      fetchEmployeeGroups();
    }
  }, [open]);

  // Payroll Group Name Change
  const handlePayrollGroupChange = (event) => {
    setFormData((prev) => ({
      ...prev,
      name: event.target.value,
    }));

    if (error) {
      setError("");
    }
  };

  // Form Validation
  const validateForm = () => {
    if (!formData.name.trim()) {
      setError("Payroll Group is required.");
      return false;
    }

    if (!formData.employeeGroupId) {
      setError("Please select an Employee Group.");
      return false;
    }

    return true;
  };
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    try {
      setSaving(true);
      setError("");
      const payload = {
        name: formData.name.trim(),
        employeeGroupId: formData.employeeGroupId,
      };

      const response = await fetch("/api/support/payroll-groups", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      const result = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(
          result?.message ||
            `Failed to create payroll group. Status: ${response.status}`,
        );
      }
      console.log("Payroll group created:", result);
      showSnackbar(
        result?.message || "Payroll Group created successfully.",
        "success",
      );
      if (onSuccess) {
        onSuccess(result);
      }

      handleClose();
    } catch (err) {
      console.error("Error creating payroll group:", err);
      setError(err.message || "Failed to create payroll group.");
      showSnackbar(err.message || "Failed to create payroll group.", "error");
    } finally {
      setSaving(false);
    }
  };
  const handleClose = () => {
    if (saving) return;

    setFormData({
      name: "",
      employeeGroupId: null,
    });

    setError("");

    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: "16px",
        },
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0, 0, 0, 0.7)",
      }}
    >
      {/* HEADER */}
      <DialogTitle
        sx={{
          backgroundColor: theme.palette.primary.main,
          color: "white",
          py: 2,
          px: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: alpha("#fff", 0.15),
              }}
            >
              <GroupIcon />
            </Box>

            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  lineHeight: 1.2,
                }}
              >
                Add New Payroll Group
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  opacity: 0.8,
                }}
              >
                Create a new payroll group
              </Typography>
            </Box>
          </Box>

          <IconButton
            onClick={handleClose}
            disabled={saving}
            sx={{
              color: "white",
              "&:hover": {
                backgroundColor: alpha("#fff", 0.1),
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* CONTENT */}
      <DialogContent
        sx={{
          p: 3,
          pt: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
          }}
        >
          {/* PAYROLL GROUP */}
          <TextField
            sx={{
              marginTop: 2,
            }}
            fullWidth
            label="Payroll Group"
            placeholder="Enter payroll group name"
            value={formData.name}
            onChange={handlePayrollGroupChange}
            required
            disabled={saving}
            autoFocus
            error={Boolean(error && !formData.name.trim())}
            helperText={
              error && !formData.name.trim()
                ? error
                : "Enter the name of the payroll group"
            }
          />

          {/* EMPLOYEE GROUP */}
          <Autocomplete
            fullWidth
            options={employeeGroups}
            loading={loadingEmployeeGroups}
            value={
              employeeGroups.find(
                (group) =>
                  Number(group.id) === Number(formData.employeeGroupId),
              ) || null
            }
            onChange={(event, selectedEmployeeGroup) => {
              if (!selectedEmployeeGroup) {
                setFormData((prev) => ({
                  ...prev,
                  employeeGroupId: null,
                }));

                return;
              }

              setFormData((prev) => ({
                ...prev,
                employeeGroupId: selectedEmployeeGroup.id,
              }));

              setError("");
            }}
            getOptionLabel={(option) => option.employeeGroup || ""}
            isOptionEqualToValue={(option, value) =>
              Number(option.id) === Number(value.id)
            }
            disabled={saving}
            renderOption={(props, option) => (
              <Box
                component="li"
                {...props}
                key={option.id}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start !important",
                  py: 1.2,
                  px: 2,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                  }}
                >
                  {option.employeeGroup}
                </Typography>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Employee Group"
                placeholder="Select employee group"
                required
                error={Boolean(error && !formData.employeeGroupId)}
                helperText={
                  error && !formData.employeeGroupId
                    ? error
                    : "Select the employee group associated with this payroll group"
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingEmployeeGroups && <CircularProgress size={20} />}

                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>
      </DialogContent>

      {/* ACTIONS */}
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          backgroundColor: alpha(theme.palette.primary.main, 0.015),
          gap: 1,
        }}
      >
        <Button
          onClick={handleClose}
          disabled={saving}
          variant="outlined"
          sx={{
            borderRadius: "8px",
            px: 2.5,
          }}
        >
          Cancel
        </Button>

        <Button
          onClick={handleSubmit}
          disabled={
            saving || !formData.name.trim() || !formData.employeeGroupId
          }
          variant="contained"
          startIcon={
            saving ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <SaveIcon />
            )
          }
          sx={{
            borderRadius: "8px",
            px: 2.5,
          }}
        >
          {saving ? "Saving..." : "Save Payroll Group"}
        </Button>
      </DialogActions>

      {/* SNACKBAR */}
      <NotificationSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Dialog>
  );
};

export default AddNewPayrollGroup;
